/**
 * ReportPage - Smart routing based on report status and user role
 *
 * Single entry point for /reports/:reportId
 * Shows appropriate UI based on status + role combination:
 * - draft → ReportEditorV2 (for analyst/admin who can edit)
 * - pending_admin → ReportEditorV2 with approvalMode="admin"
 * - client_review → ClientReviewPanel (for Bank Admin)
 * - pending_committee → ReportEditorV2 with approvalMode="committee"
 * - approved/rejected → ReportViewer (read-only for non-CRA users)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { getReportWorkflow, type ReportWorkflow } from '../../services/firestore';

// Import sub-components
import ReportEditorV2 from './components/ReportEditorV2';
import ReportViewer from './components/ReportViewer';
import ClientReviewPanel from './components/ClientReviewPanel';
import {
  updateReportSection,
  submitForAdminReview,
  adminApproveReport,
  adminRejectReport,
  skipClientReview,
  committeeApproveReport,
  committeeRejectReport,
} from '../../services/firestore';

export default function ReportPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const {
    effectiveRole,
    effectiveIsPlatformAdmin,
    effectiveIsCRAAdmin,
    effectiveIsCRAAnalyst,
    effectiveIsRatingCommittee,
    effectiveIsBankAdmin,
  } = useRoleAssumption();
  const { t } = useTranslation();

  const [report, setReport] = useState<ReportWorkflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadReport = async () => {
    if (!reportId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await getReportWorkflow(reportId);
      if (!data) {
        setError(t('reports.reportNotFound'));
        return;
      }
      setReport(data);
    } catch (err) {
      console.error('Error loading report:', err);
      setError(t('reports.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [reportId]);

  // Handler for V2 editor save
  const handleV2Save = async (content: Record<string, string>) => {
    if (!reportId || !userProfile?.uid) return;

    try {
      setIsSaving(true);
      // Update each section - pass content as SectionDraft partial
      for (const [sectionId, sectionContent] of Object.entries(content)) {
        await updateReportSection(
          reportId,
          sectionId,
          { content: sectionContent }, // Pass as object with content property
          userProfile.uid
        );
      }
      // Reload report to get updated data
      await loadReport();
    } catch (err) {
      console.error('Error saving report:', err);
      throw err; // Re-throw so ReportEditorV2 knows save failed
    } finally {
      setIsSaving(false);
    }
  };

  // Handler for V2 editor submit
  const handleV2Submit = async () => {
    if (!reportId || !userProfile?.uid) return;

    try {
      await submitForAdminReview(reportId, userProfile.uid);
      await loadReport();
    } catch (err) {
      console.error('Error submitting report:', err);
    }
  };

  // Handler for admin approve
  const handleAdminApprove = async (comments?: string) => {
    if (!reportId || !userProfile?.uid) return;
    await adminApproveReport(reportId, userProfile.uid, comments);
    await loadReport();
  };

  // Handler for admin reject
  const handleAdminReject = async (reason: string) => {
    if (!reportId || !userProfile?.uid) return;
    await adminRejectReport(reportId, userProfile.uid, reason);
    await loadReport();
  };

  // Handler for skip client review
  const handleSkipClientReview = async () => {
    if (!reportId || !userProfile?.uid) return;
    await skipClientReview(reportId, userProfile.uid);
    await loadReport();
  };

  // Handler for committee approve
  const handleCommitteeApprove = async (comments?: string) => {
    if (!reportId || !userProfile?.uid) return;
    await committeeApproveReport(reportId, userProfile.uid, comments);
    await loadReport();
  };

  // Handler for committee reject
  const handleCommitteeReject = async (reason: string) => {
    if (!reportId || !userProfile?.uid) return;
    await committeeRejectReport(reportId, userProfile.uid, reason);
    await loadReport();
  };

  // Loading state
  if (loading) {
    return (
      <div className="report-page-loading">
        <div className="loading">{t('reports.loadingReport')}</div>
      </div>
    );
  }

  // Error state
  if (error || !report) {
    return (
      <div className="report-page-error">
        <div className="error-message">{error || t('reports.reportNotFound')}</div>
        <button className="back-btn" onClick={() => navigate('/reports')}>
          {'← '}{t('reports.backToReports')}
        </button>
      </div>
    );
  }

  // Smart routing based on status + role
  const isAdmin = effectiveIsPlatformAdmin || effectiveIsCRAAdmin;
  const isAnalyst = effectiveIsCRAAnalyst;
  const isCommittee = effectiveIsRatingCommittee;
  const isBankAdmin = effectiveIsBankAdmin;

  // CRA users (analysts, admins, committee) use ReportEditorV2
  const isCRAUser = isAnalyst || isAdmin || isCommittee;

  switch (report.status) {
    case 'draft':
      // Only analyst who owns it or admin can edit
      if (report.analyst_id === userProfile?.uid || isAdmin) {
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
          />
        );
      }
      return <ReportViewer report={report} />;

    case 'pending_admin':
      if (isAdmin) {
        // Admin uses V2 with approval actions
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
            approvalMode="admin"
            onApprove={handleAdminApprove}
            onReject={handleAdminReject}
          />
        );
      }
      // Analysts see V2 read-only view
      if (isCRAUser) {
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
          />
        );
      }
      return <ReportViewer report={report} />;

    case 'client_review':
      if (isBankAdmin) {
        return <ClientReviewPanel report={report} onUpdate={loadReport} />;
      }
      // CRA Admin can skip client review
      if (isAdmin) {
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
            approvalMode="admin_skip_client"
            onSkipClientReview={handleSkipClientReview}
          />
        );
      }
      // Analysts see V2 read-only view
      if (isCRAUser) {
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
          />
        );
      }
      return <ReportViewer report={report} />;

    case 'pending_committee':
      if (isCommittee || effectiveIsPlatformAdmin) {
        // Committee uses V2 with approval actions
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
            approvalMode="committee"
            onApprove={handleCommitteeApprove}
            onReject={handleCommitteeReject}
          />
        );
      }
      // Analysts see V2 read-only view
      if (isCRAUser) {
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
          />
        );
      }
      return <ReportViewer report={report} />;

    case 'approved':
    case 'rejected':
    default:
      // CRA users see V2 read-only view
      if (isCRAUser) {
        return (
          <ReportEditorV2
            report={report}
            onSave={handleV2Save}
            onSubmit={handleV2Submit}
            isSaving={isSaving}
          />
        );
      }
      return <ReportViewer report={report} />;
  }
}
